import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { clock } from '../../contexts/FarmStatusContext';
import { curvePoints, curveAt, UNIT_RATE_INR } from '../../config/meter';
import { Panel, PanelTitle, Note } from './ui';
import { DAY, alpha, plural } from './helpers';

const PowerFacts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 18px;
  margin: -4px 0 14px;
  font-size: 13px;
  color: ${p => p.theme.colors.textSecondary};
  b { font-weight: 500; color: ${p => p.theme.colors.text}; }
`;

const PowerHead = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 18px;
  margin-bottom: 12px;

  .big { font-size: 30px; font-weight: 500; letter-spacing: -0.035em; line-height: 1; }
  .big small { font-size: 13px; font-weight: 500; letter-spacing: 0; color: ${p => p.theme.colors.textMuted}; margin-left: 4px; }
  .cmp {
    font-size: 12px; font-weight: 500; padding: 2px 8px; border-radius: 10px;
    background: ${p => p.theme.colors.surfaceHover}; color: ${p => p.theme.colors.textSecondary};
  }
  .cmp.up { background: ${p => alpha(p.theme.colors.warning, 0.16)}; color: ${p => p.theme.colors.text}; }
  .cmp.down { background: ${p => alpha(p.theme.colors.success, 0.14)}; color: ${p => p.theme.colors.text}; }
`;

/* Units used in each hour of today. Past hours neutral, the current hour lit in
   the signature colour, hours still to come drawn as a faint typical-day ghost. */
function HourBars({ hours, typical, nowHour }) {
  const theme = useTheme();
  const max = Math.max(0.05, ...hours.filter(v => v != null), ...typical.filter(v => v != null));
  const H = 72;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(24, minmax(0, 1fr))', gap: 3, alignItems: 'end', height: H }}>
        {hours.map((v, h) => {
          const future = h > nowHour;
          if (h === nowHour) {
            // The hour in progress: what is used so far, inside a ghost of a typical
            // hour, so a fresh hour does not read as zero.
            const ghost = typical[h] != null ? Math.max(3, (typical[h] / max) * 100) : 0;
            const used = v != null ? (v / max) * 100 : 0;
            return (
              <span key={h} title={`${String(h).padStart(2, '0')}:00 so far  ${v != null ? v.toFixed(2) : '0'} units${typical[h] != null ? `, a typical hour ${typical[h].toFixed(2)}` : ''}`}
                style={{ position: 'relative', height: `${Math.max(ghost, used, 3)}%`, borderRadius: 4, border: ghost > used ? `1px dashed ${theme.colors.signature}` : 'none' }}>
                <span style={{ position: 'absolute', left: -1, right: -1, bottom: -1, height: `${Math.max(6, (used / Math.max(ghost, used, 3)) * 100)}%`, borderRadius: 4, background: theme.colors.signature }} />
              </span>
            );
          }
          const val = future ? typical[h] : v;
          const pct = val != null ? Math.max(3, (val / max) * 100) : 3;
          return (
            <span key={h} title={`${String(h).padStart(2, '0')}:00  ${val != null ? `${val.toFixed(2)} units${future ? ' (typical)' : ''}` : 'no data'}`}
              style={{
                height: `${pct}%`,
                borderRadius: 4,
                background: future ? 'transparent' : alpha(theme.colors.text, val != null ? 0.16 : 0.05),
                border: future && val != null ? `1px dashed ${theme.colors.border}` : 'none',
              }} />
          );
        })}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) auto', marginTop: 6, fontSize: 10.5, color: theme.colors.textMuted }}>
        {['00:00', '06:00', '12:00', '18:00', '24:00'].map(t => <span key={t}>{t}</span>)}
      </div>
    </div>
  );
}

/* Units used so far today against yesterday and a typical day, the pace to midnight, and bars per IST hour. */
export default function PowerTodayCard({ power, profile, monthFrom, now, order }) {
  // Units used in each IST hour today (and on a typical day), from the meter's
  // cumulative 15-min curve: an hour's use is the rise across it.
  const hourly = useMemo(() => {
    const pr = profile;
    if (!pr?.day_start) return null;
    const step = pr.step_seconds || 900;
    const today = curvePoints(pr.today, pr.day_start, step);
    const typical = curvePoints(pr.typical, pr.day_start, step);
    const nowHour = Math.floor((now - pr.day_start) / 3600);
    const rise = (pts, a, b) => {
      const va = curveAt(pts, a), vb = curveAt(pts, b);
      return va == null || vb == null ? null : Math.max(0, vb - va);
    };
    const hours = [], typ = [];
    for (let h = 0; h < 24; h += 1) {
      const a = pr.day_start + h * 3600;
      hours.push(h <= nowHour ? rise(today, a, Math.min(a + 3600, now)) : null);
      typ.push(rise(typical, a, a + 3600));
    }
    // Where today stands against a typical day and yesterday at this same time.
    const yesterday = curvePoints(pr.yesterday, pr.day_start, step);
    return {
      hours, typical: typ, nowHour, any: hours.some(v => v != null),
      typicalByNow: curveAt(typical, now), yesterdayByNow: curveAt(yesterday, now),
      typicalDays: pr.typical_days || 0,
    };
  }, [profile, now]);

  // Power: today against a typical day by this time, and a month figure only once
  // the month has more than a few days behind it (on the 1st it is just today).
  // The headline comparison is yesterday at this same time: the "typical day" is a
  // mean of the last complete days and lags any change in load (new light or AC
  // schedule), so on its own it read "58 % more" on a normal day. It stays as a
  // secondary fact, with the number of days it is built from.
  const vsYesterday = power?.soFar != null && hourly?.yesterdayByNow ? (power.soFar - hourly.yesterdayByNow) / hourly.yesterdayByNow : null;
  const monthCost = power?.cost != null && monthFrom && now - monthFrom > 3 * DAY ? power.cost : null;

  return (
    <Panel $order={order}>
      <PanelTitle>Power today <Link to="/power">Power</Link></PanelTitle>
      {power?.soFar == null && !hourly?.any ? (
        <Note>The meter has not reported today. Check the meter agent on the farm Raspberry Pi.</Note>
      ) : (
        <>
          <PowerHead>
            <span className="big">{power?.soFar != null ? power.soFar.toFixed(1) : '--'}<small>units by {clock(now, now)}</small></span>
            {vsYesterday != null && (
              <span className={`cmp ${vsYesterday > 0.03 ? 'up' : vsYesterday < -0.03 ? 'down' : ''}`}
                title={`Yesterday had used ${hourly.yesterdayByNow.toFixed(1)} units by ${clock(now, now)}`}>
                {Math.abs(vsYesterday) <= 0.03 ? 'about the same as yesterday' : `${Math.round(Math.abs(vsYesterday) * 100)} % ${vsYesterday > 0 ? 'more' : 'less'} than yesterday`} by now
              </span>
            )}
          </PowerHead>
          <PowerFacts>
            {power?.soFar != null && <span><b>₹{Math.round(power.soFar * UNIT_RATE_INR).toLocaleString('en-IN')}</b> so far today</span>}
            {power?.pace != null && (
              <span title={power.paceBasis === 'schedule' ? 'Used so far plus what the light schedules say is still to come'
                : power.paceBasis === 'yesterday' ? "Used so far plus what yesterday used from this time to midnight"
                : power.paceBasis === 'typical' ? 'Used so far plus a typical day from now to midnight' : 'Used so far at the average rate since midnight'}>
                on pace for <b>~{power.pace.toFixed(0)} units</b> (₹{Math.round(power.pace * UNIT_RATE_INR).toLocaleString('en-IN')})
              </span>
            )}
            {hourly?.yesterdayByNow != null && <span>yesterday by now <b>{hourly.yesterdayByNow.toFixed(1)}</b></span>}
            {hourly?.typicalByNow != null && hourly.typicalDays > 0 && (
              <span title="Mean of the last fully recorded days at this time of day">
                typical of {plural(hourly.typicalDays, 'day')} by now <b>{hourly.typicalByNow.toFixed(1)}</b>
              </span>
            )}
            {monthCost != null && <span><b>₹{Math.round(monthCost).toLocaleString('en-IN')}</b> this month</span>}
          </PowerFacts>
          {hourly && <HourBars hours={hourly.hours} typical={hourly.typical} nowHour={hourly.nowHour} />}
        </>
      )}
    </Panel>
  );
}
